import axios from "axios";
import { apiUrl } from "features/configure";
import { useConfirm } from "features/home/ConfirmDialogProvider";
import useDispatchSnackbar from "hooks/useDispatchSnackbar";
import useNavigateEntity from "hooks/useNavigateEntity";
import { get } from "lodash";
import React, { useEffect, useState } from "react";
import { useOutletContext } from "react-router";
import { StyledAiThinking } from "./TestGround.styles";
import { v4 as uuidv4 } from "uuid";
import useGetPagePermission from "hooks/useGetPagePermission";

export default function useStartTestGround() {
  const [messagesList, setMessagesList] = React.useState([]);
  const [entityName] = useOutletContext();
  const { navigateEntity } = useNavigateEntity();
  const dispatchSnackbar = useDispatchSnackbar();
  const confirm = useConfirm();
  const [canSend, setCanSend] = React.useState(true);
  const [message, setMessage] = React.useState("");
  const [hasKnowledgeBase, setHasKnowledgeBase] = useState(false);

  const pagePermission = useGetPagePermission();
  const { readonly } = pagePermission("aiManagement");

  async function startTestGround() {
    try {
      setCanSend(true);
      setMessage("");
      setMessagesList([]);
      const { data } = await axios.post(
        `${apiUrl}/api/widget/${entityName}/startTestGroundChat`
      );

      const startingMessages = data?.messages?.results.map((item) => {
        return {
          ...item,
          _id: item?._id,
          isAdminResponse: item.isAdminResponse,
          text: item.message,
          time: item.created,
          data: item?.data,
          role: item?.role,
        };
      });
      setMessagesList((prev) => [...prev, ...startingMessages]);
    } catch (err) {
      if (
        get(err, "response.data.message", "") ===
        "No widget or ticket bot configured"
      ) {
        return confirm({
          title: "Set Up Your Bot First",
          content: "There is no widget or ticket bot configured",
          confirmText: "Go to Integration",
          color: "secondary",
        })
          .then(() => {
            navigateEntity("/settings/general/integration");
          })
          .catch(() => {});
      }
      dispatchSnackbar({ err });
    }
  }

  async function handleRestart() {
    setCanSend(true);
    setMessage("");
    setMessagesList([]);
    const { data } = await axios.post(
      `${apiUrl}/api/widget/${entityName}/restartTestGroundChat`
    );

    const startingMessages = data?.messages?.results.map((item) => {
      return {
        ...item,
        _id: item?._id,
        isAdminResponse: item.isAdminResponse,
        text: item.message,
        time: item.created,
        data: item?.data,
        role: item?.role,
      };
    });
    setMessagesList((prev) => [...prev, ...startingMessages]);
  }

  async function handleSendMessage(message) {
    if (!canSend || readonly) return;

    const _id = uuidv4();

    setMessagesList((prev) => [
      ...prev,
      { _id, isAdminResponse: false, text: message, time: new Date() },
    ]);

    setMessage("");
    setCanSend(false);
    const thinkingMessageIndex = insertThinkingMessage();
    const { data } = await axios.post(
      `${apiUrl}/api/widget/${entityName}/testGroundChat`,
      {
        message,
      }
    );

    simulateAiStreamingReply(data, thinkingMessageIndex);
  }

  function insertThinkingMessage() {
    let thinkingIndex = messagesList.length + 1;
    setMessagesList((prev) => {
      const newMsg = {
        isAdminResponse: true,
        text: (
          <StyledAiThinking>
            <span />
            <span />
            <span />
          </StyledAiThinking>
        ),
        time: new Date(),
        isTexting: true,
      };
      return [...prev, newMsg];
    });
    return thinkingIndex;
  }

  function simulateAiStreamingReply(data, index) {
    let currentIndex = 0;
    const { message: fullText = "" } = data;
    setMessagesList((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...data,
        ...updated[index],
        text: "",
        isTexting: true, // 額外加個 flag 來辨別
      };
      return updated;
    });

    const interval = setInterval(() => {
      currentIndex++;

      setMessagesList((prev) => {
        const updated = [...prev];
        // 如果已經輸出完，直接顯示完整文字

        updated[index] = {
          ...updated[index],
          text: fullText.slice(0, currentIndex),
        };
        if (currentIndex >= fullText.length) {
          updated[index] = {
            ...updated[index],
            text: fullText,
            isTexting: false,
          };
          clearInterval(interval);
          setCanSend(true);
        }
        return updated;
      });
    }, 30); // 每 30 毫秒更新一次，可依需求調整
  }

  async function fetchAiSetting() {
    try {
      const { data } = await axios.get(
        `${apiUrl}/api/manage/aiSupport/${entityName}`
      );
      const { knowledgeBaseResourceCount } = data;
      setHasKnowledgeBase(knowledgeBaseResourceCount > 0);
    } catch (err) {
      dispatchSnackbar({ err });
    }
  }

  useEffect(() => {
    fetchAiSetting();
    startTestGround();
  }, []);

  return {
    message,
    setMessage,
    canSend,
    setCanSend,
    messagesList,
    handleRestart,
    handleSendMessage,
    hasKnowledgeBase,
    setMessagesList,
  };
}
